import { useState } from "react";
import { Crown, Check, X, ArrowRight, RefreshCcw, Sparkles } from "lucide-react";
import { createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

export const UPGRADE_MODAL = {
  modal: "upgrade-modal",
  title: "upgrade-modal-title",
  monthlyBtn: "upgrade-modal-monthly",
  annualBtn: "upgrade-modal-annual",
  notNowBtn: "upgrade-modal-not-now",
};

const BENEFITS = [
  "Unlimited children",
  "Family Insights (AI)",
  "Family Health Score",
  "Momentum Score",
  "Health History",
  "Premium alerts",
];

/**
 * Friendly upgrade modal shown when a Free user hits a plan limit (HTTP 402).
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   reason?: string  (optional explanatory copy from the 402)
 *   pendingChild?: object  (the child data the parent was trying to add — preserved
 *                          for after-upgrade resume; modal does not destroy it)
 */
export default function UpgradeModal({ open, onClose, reason }) {
  const [busy, setBusy] = useState(null); // "monthly" | "annual" | null

  if (!open) return null;

  const go = async (plan_id) => {
    setBusy(plan_id === "family_plus_monthly" ? "monthly" : "annual");
    try {
      const { url } = await createCheckoutSession(plan_id);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={UPGRADE_MODAL.modal}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(22,39,31,0.55)] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="fcc-card w-full max-w-2xl overflow-hidden relative animate-in fade-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--fcc-cream-2)] flex items-center justify-center text-[var(--fcc-muted)]"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 sm:p-10">
          <span className="fcc-pill fcc-chip-emma"><Crown className="w-3 h-3" /> Family Plus</span>
          <h2
            data-testid={UPGRADE_MODAL.title}
            className="font-display text-4xl sm:text-5xl leading-tight text-[var(--fcc-ink)] mt-5"
          >
            Upgrade to Family Plus
          </h2>
          <p className="text-[var(--fcc-ink-soft)] mt-4 leading-relaxed">
            {reason || "Free accounts include 1 child. Upgrade to Family Plus to add unlimited children and unlock premium family insights."}
          </p>

          <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
            {BENEFITS.map(b => (
              <li key={b} className="flex items-center gap-2 text-sm text-[var(--fcc-ink)]">
                <Check className="w-4 h-4 text-[var(--fcc-green)]" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <button
              data-testid={UPGRADE_MODAL.monthlyBtn}
              onClick={() => go("family_plus_monthly")}
              disabled={busy !== null}
              className="fcc-btn-primary justify-center disabled:opacity-50"
            >
              {busy === "monthly" ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
              Upgrade Monthly — $9.99/mo
            </button>
            <button
              data-testid={UPGRADE_MODAL.annualBtn}
              onClick={() => go("family_plus_annual")}
              disabled={busy !== null}
              className="fcc-btn-outline justify-center disabled:opacity-50"
              style={{ borderColor: "var(--fcc-green)" }}
            >
              {busy === "annual" ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Upgrade Annual — $99/yr <span className="text-xs">· best value</span>
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              data-testid={UPGRADE_MODAL.notNowBtn}
              onClick={onClose}
              disabled={busy !== null}
              className="text-sm text-[var(--fcc-muted)] hover:underline disabled:opacity-50"
            >
              Not now
            </button>
          </div>

          <div className="mt-7 pt-5 border-t border-[var(--fcc-border)] text-center text-[11px] text-[var(--fcc-muted)] leading-relaxed">
            Powered by All IT Solutions LLC · Smart people, smart solutions <br />
            Secure checkout by Stripe · Cancel any time <ArrowRight className="inline w-3 h-3" />
          </div>
        </div>
      </div>
    </div>
  );
}
